import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchQuickQuestions, submitQuick } from "@/lib/api";

const QuickAssessmentDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const questionsQuery = useQuery({
    queryKey: ["quick-questions"],
    queryFn: fetchQuickQuestions,
    enabled: open,
    staleTime: Infinity,
  });
  const data = questionsQuery.data;

  const handleOpenChange = (next) => {
    if (!next) {
      setStep(0);
      setAnswers({});
      setResult(null);
      setEmail("");
      setName("");
    }
    onOpenChange(next);
  };

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogTitle>Loading…</DialogTitle>
        </DialogContent>
      </Dialog>
    );
  }

  const totalSteps = data.questions.length + 1;
  const isEmailStep = step === data.questions.length;
  const q = isEmailStep ? null : data.questions[step];

  const handleAnswer = (val) => {
    setAnswers((a) => ({ ...a, [q.id]: val }));
    setTimeout(() => setStep(step + 1), 150);
  };

  const submit = async () => {
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email."); return; }
    setSubmitting(true);
    try {
      const r = await submitQuick({ email, name, answers });
      setResult(r);
    } catch (e) {
      toast.error("Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {!result ? (
          <div className="p-6">
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-500 mb-2">60-Second Pulse</div>
            <DialogTitle className="font-display text-xl font-semibold text-slate-900 mb-1">
              {isEmailStep ? "Almost there — one last detail" : `Question ${step + 1} of ${data.questions.length}`}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 mb-5">
              {isEmailStep ? "We use this to follow up if it would help — your score appears on the next screen." : q.category}
            </DialogDescription>

            <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-6">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
            </div>

            {!isEmailStep ? (
              <>
                <p className="text-base font-medium text-slate-900 leading-snug mb-5">{q.text}</p>
                <div className="space-y-2">
                  {data.answer_scale.map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`quick-${q.id}-${opt.value}`}
                      onClick={() => handleAnswer(opt.value)}
                      className="w-full text-left p-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-slate-400 group-hover:text-blue-600">{opt.value}</span>
                        <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt.label}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Input data-testid="quick-name" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
                <Input data-testid="quick-email" placeholder="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Button onClick={submit} disabled={submitting} data-testid="quick-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <>Get my pulse score <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
                <p className="text-[11px] text-slate-400 text-center pt-2">For a complete report, take the full 12-question assessment. See our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 underline">privacy practices</a>.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <DialogTitle className="font-display text-2xl font-semibold text-slate-900 mb-1">Your pulse score</DialogTitle>
            <div className="font-display text-5xl font-bold text-slate-900 tracking-tighter mt-4" data-testid="quick-result-score">{result.overall_score}<span className="text-slate-400 text-xl">/100</span></div>
            <div className="mt-3 inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">{result.readiness_band}</div>
            <p className="mt-5 text-sm text-slate-600">This is a directional read. The full 12-question assessment gives you a scored report across all six dimensions plus an executive narrative.</p>
            <div className="mt-6 flex gap-2">
              <Button onClick={() => { onOpenChange(false); window.location.href = "/assessment"; }} data-testid="quick-go-full-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Take the full assessment</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickAssessmentDialog;
